import {
  put, takeLatest, all, call, race, take, select
} from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { STATUS_SUBMITTED } from 'constants/enums/applicationStatuses';
import env from 'config/environment';
import { api } from '../../../services';
import {
  AnnouncementsFailureResponse,
  ApiVersionFailureResponse,
  EnvironmentFailureResponse,
  FetchFormSuccessAction,
  FetchCheckStatusFailureResponse,
  FetchCheckStatusRequestAction,
  FetchCheckStatusResponse,
  FetchIdentityStatusFailureResponse,
  FetchIdentityStatusRequestAction,
  FetchIdentityStatusResponse,
  FetchSetAccountFailureResponse,
  FetchSetAccountRequestAction,
  FetchSetAccountSuccessAction,
  FetchSetAccountSuccessResponse,
  InstitutionSearchFailureResponse,
  ServerTimeFailureResponse,
  StatusFailureResponse,
  fetchCheckStatusFailure,
  fetchCheckStatusSuccess,
  fetchIdentityStatusFailure,
  fetchIdentityStatusSuccess,
  fetchSetAccountSuccess,
  fetchSetAccountFailure,
  fetchStatusFailure,
  fetchStatusRequest,
  fetchStatusSuccess,
  FetchStatusRequestAction,
  StatusResponse,
  FetchFormRequestAction,
  fetchFormFailure,
  fetchFormSuccess,
  FormResponse,
  fetchFormRequest,
  EnvironmentResponse,
  FetchEnvironmentRequestAction,
  fetchEnvironmentSuccess,
  fetchEnvironmentFailure,
  FetchApiVersionRequestAction,
  fetchApiVersionSuccess,
  fetchApiVersionFailure,
  fetchApiVersionRequest,
  ApiVersionResponse,
  FetchServerTimeRequestAction,
  ServerTimeResponse,
  fetchServerTimeFailure,
  fetchServerTimeSuccess,
  FetchInstitutionSearchRequestAction,
  fetchInstitutionSearchSuccess,
  fetchInstitutionSearchFailure,
  InstitutionSearchResponse,
  FetchAnnouncementsRequestAction,
  AnnouncementsResponse,
  fetchAnnouncementsFailure,
  fetchAnnouncementsSuccess,
  SubmitIdentityVerificationRequestAction,
  SubmitIdentityVerificationResponse,
  submitIdentityVerificationSuccess,
  submitIdentityVerificationFailure,
  SubmitIdentityVerificationFailureResponse
} from './actions';
import * as ActionTypes from './actionTypes';
import { RootActionTypes } from '../actionTypes';
import { RootState } from '../reducer';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onCheckStatusRequest(action: FetchCheckStatusRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.checkStatus)) as FetchCheckStatusResponse;
    yield put(fetchCheckStatusSuccess(response));
  } catch (err) {
    yield put(fetchCheckStatusFailure(err as FetchCheckStatusFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onIdentityStatusRequest(action: FetchIdentityStatusRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.identityStatus)) as FetchIdentityStatusResponse;
    yield put(fetchIdentityStatusSuccess(response));
  } catch (err) {
    yield put(fetchIdentityStatusFailure(err as FetchIdentityStatusFailureResponse));
  }
}

export function* onSetAccountRequest(action: FetchSetAccountRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response: FetchSetAccountSuccessResponse = (yield call(api.setAccount, action.payload)) as FetchSetAccountSuccessResponse;
    yield put(fetchSetAccountSuccess(response, action?.payload?.status));
  } catch (err) {
    yield put(fetchSetAccountFailure(err as FetchSetAccountFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onSetAccountSuccess(action: FetchSetAccountSuccessAction): SagaIterator {
  if (env.IsPWTest()) { return; }
  if (action?.status !== STATUS_SUBMITTED) {
    yield put(fetchFormRequest());
  }
  // Do not attempt to load a form if the status was submitted they should be redirected via UI to locked.
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onStatusRequest(action: FetchStatusRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.status)) as StatusResponse;
    yield put(fetchStatusSuccess(response));
  } catch (err) {
    yield put(fetchStatusFailure(err as StatusFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onEnvironmentRequest(action: FetchEnvironmentRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.environment)) as EnvironmentResponse;
    yield put(fetchEnvironmentSuccess(response));
  } catch (err) {
    yield put(fetchEnvironmentFailure(err as EnvironmentFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onFormRequest(action: FetchFormRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.form)) as FormResponse;
    yield put(fetchFormSuccess(response));
  } catch (err) {
    console?.warn?.('Fetch Form failed, ', err);
    yield put(fetchFormFailure(err));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onFormSuccess(action: FetchFormSuccessAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  yield put(fetchStatusRequest());
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onApiVersionRequest(action: FetchApiVersionRequestAction): SagaIterator {
  if (env.IsPWTest()) { return; }

  try {
    const response = (yield call(api.apiVersion)) as ApiVersionResponse;
    yield put(fetchApiVersionSuccess(response));
  } catch (err) {
    console?.warn?.('Fetch Api Version failed, ', err);
    yield put(fetchApiVersionFailure(err as ApiVersionFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onServerTimeRequest(action: FetchServerTimeRequestAction): SagaIterator {
  try {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const response = (yield call(api.getServerTime)) as ServerTimeResponse;
    yield put(fetchServerTimeSuccess(response));
  } catch (err) {
    console?.warn?.('Fetch Server Time failed, ', err);
    yield put(fetchServerTimeFailure(err as ServerTimeFailureResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onAnnouncementsRequest(action: FetchAnnouncementsRequestAction): SagaIterator {
  try {
    const response = (yield call(api.getAnnouncements)) as AnnouncementsResponse;
    yield put(fetchAnnouncementsSuccess(response));
  } catch (err) {
    console?.warn?.('Fetch Announcements failed, ', err);
    yield put(fetchAnnouncementsFailure(err as AnnouncementsFailureResponse));
  }
}

export function* onInstitutionSearchRequest(action: FetchInstitutionSearchRequestAction): SagaIterator {
  try {
    if (action?.payload) {
      const response = (yield call(api.getInstituteSearch, action.payload)) as InstitutionSearchResponse;
      yield put(fetchInstitutionSearchSuccess(response));
    }
  } catch (err) {
    if ((err as InstitutionSearchFailureResponse)?.data?.errors) {
      yield put(fetchInstitutionSearchFailure(err as InstitutionSearchFailureResponse));
    } else {
      console?.warn?.('getInstituteSearch failed, ', err);
    }
  }
}

export function* watcherWhenLoggedIn(): SagaIterator {
  // In the scenario we need to support changes of status, we'll need to configure multiple watchers.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const status = yield select((state: RootState) => state.application.Settings.status);
  if (status === STATUS_SUBMITTED) { return; }

  // if (!env.LoginRedirectEnabled()) {
  // FetchFormRequest action doesn't get dispatched anymore after loggedIn, as
  // it seems to create a memory issue during tests. We call the method directly
  // instead.
  // yield call(onFormRequest, fetchFormRequest());
  // }
  yield call(onApiVersionRequest, fetchApiVersionRequest());
  yield all([
    takeLatest(ActionTypes.FETCH_FORM_REQUEST, onFormRequest),
    takeLatest(ActionTypes.FETCH_FORM_SUCCESS, onFormSuccess)
  ]);
}

export function* onLoggedIn(): SagaIterator {
  yield race({
    request: call(watcherWhenLoggedIn),
    logout: take(RootActionTypes.authentication.LOGGED_OFF),
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function* onSubmitIdentityVerificationRequest(action: SubmitIdentityVerificationRequestAction): SagaIterator {
  try {
    const response = (yield call(api.submitIdentityVerification, action.payload)) as SubmitIdentityVerificationResponse;
    yield put(submitIdentityVerificationSuccess(response));
  } catch (err) {
    console?.warn?.('Submit identity verification failed, ', err);
    yield put(submitIdentityVerificationFailure(err as SubmitIdentityVerificationFailureResponse));
  }
}

export default function* rootSaga(): SagaIterator {
  yield all([
    takeLatest(RootActionTypes.authentication.LOGGED_IN, onLoggedIn),
    takeLatest(ActionTypes.FETCH_CHECK_STATUS_REQUEST, onCheckStatusRequest),
    takeLatest(ActionTypes.FETCH_IDENTITY_STATUS_REQUEST, onIdentityStatusRequest),
    takeLatest(ActionTypes.FETCH_SET_ACCOUNT_REQUEST, onSetAccountRequest),
    takeLatest(ActionTypes.FETCH_SET_ACCOUNT_SUCCESS, onSetAccountSuccess),
    takeLatest(ActionTypes.FETCH_STATUS_REQUEST, onStatusRequest),
    takeLatest(ActionTypes.FETCH_ENVIRONMENT_REQUEST, onEnvironmentRequest),
    takeLatest(ActionTypes.FETCH_API_VERSION_REQUEST, onApiVersionRequest),
    takeLatest(ActionTypes.FETCH_SERVER_TIME_REQUEST, onServerTimeRequest),
    takeLatest(ActionTypes.FETCH_INSTITUTION_SEARCH_REQUEST, onInstitutionSearchRequest),
    takeLatest(ActionTypes.FETCH_ANNOUNCEMENTS_REQUEST, onAnnouncementsRequest),
    takeLatest(ActionTypes.SUBMIT_IDENTITY_VERIFICATION_REQUEST, onSubmitIdentityVerificationRequest),
  ]);
}
